import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface AnimatedNumberProps {
  value: number;
  format?: (value: number) => string;
  className?: string;
  duration?: number;
}

export function AnimatedNumber({
  value,
  format = (v) => v.toString(),
  className = 'inline-block',
}: AnimatedNumberProps) {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationFrameIdRef = useRef<number>(null);

  useEffect(() => {
    if (value === displayValue) {
      return;
    }

    const startTime = Date.now();
    const startValue = displayValue;
    const endValue = value;
    const diff = endValue - startValue;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const x = Math.min(elapsed / (3 * 1000), 1);

      // Default ease-out-expo-like curve, can be customized via props
      const easeProgress = cubicBezier(0.94, 0.126, 0, 0.997)(x);
      const currentValue = startValue + diff * easeProgress;

      setDisplayValue(currentValue);

      if (x < 1) {
        animationFrameIdRef.current = requestAnimationFrame(animate);
      }
    };

    setIsAnimating(true);
    animationFrameIdRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, [value]);

  return (
    <motion.span
      className={className}
      animate={isAnimating ? { scale: [1, 1 /*.03*/, 1] } : {}}
      onAnimationComplete={() => setIsAnimating(false)}
      transition={{ duration: 0.2 }}
    >
      {format(displayValue)}
    </motion.span>
  );
}

// Solves for x in BezierX(t) = x using Newton-Raphson, then gets y from BezierY(t)
function cubicBezier(x1: number, y1: number, x2: number, y2: number): (x: number) => number {
  // Coefficients for cubic Bézier
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;

  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;

  const bezierX = (t: number): number => ((ax * t + bx) * t + cx) * t;
  const bezierXDerivative = (t: number): number => (3 * ax * t + 2 * bx) * t + cx;
  const bezierY = (t: number): number => ((ay * t + by) * t + cy) * t;

  // Invert bezierX(t) = x via Newton-Raphson
  const solveT = (x: number): number => {
    let t = x; // Initial guess
    for (let i = 0; i < 5; i++) {
      const xEstimate = bezierX(t) - x;
      const dx = bezierXDerivative(t);
      if (Math.abs(xEstimate) < 1e-6 || dx === 0) break;
      t -= xEstimate / dx;
    }
    return t;
  };

  // Final function maps input x ∈ [0,1] to y ∈ [0,1]
  return (x: number): number => bezierY(solveT(x));
}
